import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import {
  createUser,
  deleteUser,
  exportUsers,
  generateRecoveryToken,
  getUsers,
  updateUser,
} from '@go-client/lib/api/user';
import { Property } from '@go-shared/types/property';
import { User, UserCreateParams } from '@go-shared/types/user';
import Page from './Page';
import { MenuBar } from '@go-client/components';
import {
  BubbleButton,
  BubbleCheckbox,
  BubbleFormGroup,
  BubbleInput,
  BubbleMask,
  BubbleOverlay,
  BubbleSelect,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import {
  BubbleTypeaheadOption,
  BubbleTypeaheadValue,
} from '@go-client/components/BubbleUI/BubbleTypeahead';
import BubbleValidated, {
  BubbleValidatedRules,
  BubbleValidatedStatus,
} from '@go-client/components/BubbleUI/BubbleValidated';
import { usePropertyInfo } from '@go-client/context/PropertyInfoContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { getSimpleStaffTemplates } from '@go-client/lib/api/staffTemplate';
import { getSimpleVenuesTemplates } from '@go-client/lib/api/venueTemplate';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import { cleanUsername, getUserProperties } from '@go-shared/lib/user';
import {
  safeIdCompare,
  copyToClipboard,
  findInitials,
  hashStringToColor,
} from '@go-shared/lib/utils';
import { SimpleStaffTemplate } from '@go-shared/types/staffTemplate';
import { SimpleVenueTemplate } from '@go-shared/types/venueTemplate';
import { Alert, Dropdown, DropdownItem } from '@spark-labs/avocado-ui';
import Fuse from 'fuse.js';
import uuid from 'uuid';
import PageWrapper from './PageWrapper';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import PropertiesNavigation from '@go-client/components/Navigations/PropertiesNavigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './UserManagement.scss';

export interface UserManagementParams {
  property: string;
}

type UserForm = {
  mode: 'edit' | 'create';
  username: string;
  initialProperty: Property;
  firstName: string;
  lastName: string;
  email: string;
  shipAdmin: boolean;
  groupAdmin: boolean;
  propertyAdmin: boolean;
  groupUser: boolean;
  groupRequester: boolean;
  reserveAdmin: boolean;
  reserveUser: boolean;
  host: boolean;
  reservationManager: boolean;
  libraryAdmin: boolean;
  feedEditor: boolean;
  canvasEditor: boolean;
  brandAdmin: boolean;
  viewOnly: boolean;
  groupApprovingVenues: Array<BubbleTypeaheadValue<SimpleVenueTemplate>>;
  staffRole?: SimpleStaffTemplate;
  userAdmin: boolean;
  propertyAccessList: Array<BubbleTypeaheadValue<Property>>;
  _dirty: boolean;
};

export type UserManagementPageProps = RouteComponentProps<UserManagementParams>;

interface UserManagementDataBundle {
  users: User[];
  staffTemplates: SimpleStaffTemplate[];
  venueTemplates: SimpleVenueTemplate[];
  venueTemplateOpts: Array<BubbleTypeaheadOption<SimpleVenueTemplate>>;

  // helpful maps
  usersMap: { [key: string]: User };
  staffTemplatesMap: { [key: string]: SimpleStaffTemplate };
  venueTemplatesMap: { [key: string]: SimpleVenueTemplate };
  venueTemplateOptsMap: { [key: string]: BubbleTypeaheadOption<SimpleVenueTemplate> };
}

export default function UserManagement(props: UserManagementPageProps) {
  const [dataBundle, setDataBundle] = useState<UserManagementDataBundle | undefined>(undefined);
  const [recoveryToken, setRecoveryToken] = useState<string | undefined>(undefined);
  const [userForm, setUserForm] = useState<UserForm | undefined>(undefined);
  const [activeUser, setActiveUser] = useState<User | undefined>(undefined);
  const [search, setSearch] = useState('');

  const contentRef = useRef<HTMLDivElement | null>(null);

  const { allProperties: properties } = usePropertyInfo();
  const { brand, property: instanceProperty } = useServerInfo();

  const userFormRules: BubbleValidatedRules<UserForm> = {
    firstName: ['required'],
    email: ['required', 'lowercased'],
    // FIXME: retroactively adding the 'lowercased' to username may cause issues
    // since username cannot be changed for an existing user and, if it already had
    // it uppercased, then you would not be able to edit the user (but you cant fix it)
    username: ['required'],
  };

  useEffect(() => {
    fetchDataBundle().catch(console.error);
  }, [brand]);

  // this will reset the form each time a new user is clicked
  useEffect(() => {
    resetUserForm(activeUser);
  }, [activeUser]);

  // this will refresh the currently active user if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;

    setActiveUser(state =>
      state ? dataBundle.usersMap[state._id] || dataBundle.users[0] : dataBundle.users[0]
    );
  }, [dataBundle]);

  // this will scroll the page to top when search is modified
  useEffect(() => {
    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [search, contentRef]);

  // this will refresh the fuse database if the users have changed
  const fuse = useMemo(() => {
    if (!dataBundle) return undefined;

    return new Fuse(dataBundle.users, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['username', 'firstName', 'lastName'],
    });
  }, [dataBundle]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredUsers = !dataBundle ? [] : fuseSearchResults || dataBundle.users;
  const propertiesMap = mapBy(properties, '_id');

  async function fetchDataBundle() {
    const [users, staffTemplates, venueTemplates] = await Promise.all([
      // note: the list of users is filtered to only include those interested
      instanceProperty ? getUsers(instanceProperty._id) : getUsers(),
      getSimpleStaffTemplates(),
      getSimpleVenuesTemplates(),
    ]);
    const sortedUsers = users.reverse().filter(u => !u.disabled);
    const venueTemplateOpts = venueTemplates
      .filter(vt => !vt.archived)
      .map(t => ({ key: t._id, item: t }));

    setDataBundle({
      users: sortedUsers,
      staffTemplates,
      venueTemplates,
      venueTemplateOpts,
      usersMap: mapBy(sortedUsers, '_id'),
      staffTemplatesMap: mapBy(staffTemplates, '_id'),
      venueTemplatesMap: mapBy(venueTemplates, '_id'),
      venueTemplateOptsMap: mapBy(venueTemplateOpts, 'key'),
    });
  }

  function resetUserForm(user?: User) {
    if (!dataBundle) {
      setUserForm(undefined);
      return;
    }

    // initial property is the 'source' property of a user where the
    // staff allocations and venue approval list point to; although not
    // explicit in the model, it is assumed to be the very first property
    // of the access list of a property user. this should not be used
    // for brand users in any meaningful way
    let initialProperty = properties[0];

    switch (user?.role.level) {
      case 'property':
        initialProperty = propertiesMap[user.role.access[0]];
        break;
    }

    const formData: UserForm = {
      mode: user ? 'edit' : 'create',
      _dirty: false,
      username: user?.username ?? '',
      initialProperty,
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
      shipAdmin: user?.shipAdmin ?? false,
      groupAdmin: user?.groupAdmin ?? false,
      groupUser: user?.groupUser ?? false,
      groupRequester: user?.groupRequester ?? false,
      reserveAdmin: user?.reserveAdmin ?? false,
      reserveUser: user?.reserveUser ?? false,
      host: user?.host ?? false,
      reservationManager: user?.reservationManager ?? false,
      libraryAdmin: user?.libraryAdmin ?? false,
      feedEditor: user?.feedEditor ?? false,
      canvasEditor: user?.canvasEditor ?? false,
      brandAdmin: user?.brandAdmin ?? false,
      propertyAdmin: user?.propertyAdmin ?? false,
      viewOnly: user?.viewOnly ?? false,
      staffRole: user && user.staffRole ? dataBundle.staffTemplatesMap[user.staffRole] : undefined,
      groupApprovingVenues: [],
      userAdmin: user?.userAdmin ?? false,
      propertyAccessList: user
        ? getUserProperties(user, properties).map((p, idx) => ({
            key: p._id,
            refKey: p._id,
            item: p,
            readonly: idx === 0,
          }))
        : [],
    };

    if (user && user.groupApprovingVenues) {
      const activeGroupApprovingVenues = user.groupApprovingVenues
        .map(gpv => dataBundle.venueTemplatesMap[gpv])
        .filter(notUndefined);

      formData.groupApprovingVenues = activeGroupApprovingVenues
        .map(gav => {
          const refOpt = dataBundle.venueTemplateOptsMap[gav._id];
          return refOpt
            ? {
                key: uuid(),
                item: gav,
                refKey: refOpt.key,
              }
            : undefined;
        })
        .filter(notUndefined);
    }

    setUserForm(formData);
    setRecoveryToken(undefined);
  }

  async function promoteUserToBrand(user: User) {
    const updatedUser: User = {
      ...user,
      role: {
        level: 'brand',
        access: [brand._id],
      },
    };

    const yes = await Alert.question(
      [
        `Are you sure you want to grant access to all properties to user ${user.username}?`,
        `This action is permanent and cannot be undone. To revoke access later,`,
        `the user would need to be deleted and re-created.`,
      ].join(' ')
    );

    if (!yes) return;

    await updateUser(updatedUser);
    await fetchDataBundle();
  }

  async function softDeleteUser(user: User) {
    const yes = await Alert.question(
      `Are you certain you wish to delete the user ${user.username}?  Please be aware that this action is permanent and cannot be undone.`
    );

    if (!yes) return;

    await deleteUser(user._id);
    await fetchDataBundle();
  }

  function changeUserForm<T extends keyof UserForm>(key: T, val: UserForm[T]) {
    if (!userForm || ['mode', '_dirty'].includes(key)) return;

    setUserForm({
      ...userForm,
      [key]: val,
      _dirty: true,
    });
  }

  function handleOnClickNewUser() {
    setActiveUser(undefined);
    resetUserForm();
  }

  function handleOnPropertySelect(property: Property) {
    if (!userForm) return;

    setUserForm({
      ...userForm,
      initialProperty: property,
      staffRole: undefined,
      groupApprovingVenues: [],
    });
  }

  function handleOnStaffRoleSelect(staffTemplate?: SimpleStaffTemplate) {
    if (!userForm || !dataBundle) return;

    setUserForm({
      ...userForm,
      staffRole: staffTemplate,
      _dirty: true,
    });
  }

  function handleOnAddPropToAccess(value: BubbleTypeaheadValue<Property>) {
    if (!userForm) return;

    const newPropAccessList = [...userForm.propertyAccessList, value];

    setUserForm({
      ...userForm,
      propertyAccessList: newPropAccessList,
      _dirty: true,
    });
  }

  function handleOnDeletePropFromAccess(value: BubbleTypeaheadValue<Property>) {
    if (!userForm || value.item._id === userForm.initialProperty._id) return;

    const newPropertyAccessList = [...userForm.propertyAccessList].filter(
      val => val.key !== value.key
    );

    setUserForm({
      ...userForm,
      propertyAccessList: newPropertyAccessList,
      _dirty: true,
    });
  }

  function handleOnAddGroupApprovingVenues(value: BubbleTypeaheadValue<SimpleVenueTemplate>) {
    if (!userForm) return;

    const newGroupApprovingVenues = [...userForm.groupApprovingVenues, value];

    setUserForm({
      ...userForm,
      groupApprovingVenues: newGroupApprovingVenues,
      _dirty: true,
    });
  }

  function handleOnDeleteGroupApprovingVenues(value: BubbleTypeaheadValue<SimpleVenueTemplate>) {
    if (!userForm) return;

    const newGroupApprovingVenues = [...userForm.groupApprovingVenues].filter(
      val => val.key !== value.key
    );

    setUserForm({
      ...userForm,
      groupApprovingVenues: newGroupApprovingVenues,
      _dirty: true,
    });
  }

  async function handleGenerateRecoveryToken() {
    if (!activeUser) return;
    const recTk = await generateRecoveryToken(activeUser);
    setRecoveryToken(recTk);
  }

  async function handleSubmitUserForm() {
    if (!userForm) return;

    const userEdits = {
      firstName: userForm.firstName,
      lastName: userForm.lastName,
      email: userForm.email,
      shipAdmin: userForm.shipAdmin,
      groupAdmin: userForm.groupAdmin,
      groupUser: userForm.groupUser,
      groupRequester: userForm.groupRequester,
      reserveAdmin: userForm.reserveAdmin,
      reserveUser: userForm.reserveUser,
      host: userForm.host,
      reservationManager: userForm.reservationManager,
      libraryAdmin: userForm.libraryAdmin,
      propertyAdmin: userForm.propertyAdmin,
      feedEditor: userForm.feedEditor,
      canvasEditor: userForm.canvasEditor,
      brandAdmin: userForm.brandAdmin,
      viewOnly: userForm.viewOnly,
      staffRole: userForm.staffRole ? userForm.staffRole._id : undefined,
      groupApprovingVenues: userForm.groupApprovingVenues.map(gav => gav.item._id),
      userAdmin: userForm.userAdmin,
    };

    if (userForm.mode === 'edit') {
      if (!activeUser) return;

      const updatedUser: User = {
        ...activeUser,
        ...userEdits,
      };

      switch (updatedUser.role.level) {
        case 'property':
          updatedUser.role.access = userForm.propertyAccessList.map(pal => pal.item._id);
      }

      await updateUser(updatedUser);
      await fetchDataBundle();
    } else {
      const newUser: UserCreateParams = {
        username: cleanUsername(userForm.username),
        password: uuid(),
        role: {
          level: 'property',
          access: [userForm.initialProperty._id],
        },
        ...userEdits,
      };

      const user = await createUser(newUser);
      await fetchDataBundle();
      setActiveUser(user);
    }
  }

  const propertyOpts: Array<BubbleTypeaheadOption<Property>> = properties.map(p => ({
    key: p._id,
    item: p,
  }));

  const dropdownItems = [
    { key: 'create', element: 'New User' },
    { key: 'export', element: 'Export Users' },
  ].filter(notUndefined);

  async function onAction(action: DropdownItem) {
    switch (action.key) {
      case 'create':
        handleOnClickNewUser();
        break;
      case 'export':
        exportUsers();
        break;
    }
  }

  return (
    <PageWrapper {...props}>
      <Page className="UserManagement absolute flex items-stretch">
        <MenuBar {...props} />
        <div
          ref={contentRef}
          className="UserManagementContent bg-blue-lightest overflow-y-auto flex-grow"
        >
          <div
            className="UserManagementHeader align-with-logo flex items-end sticky top-0
            left-0 z-10 bg-white shadow"
          >
            <BrandLogo logo={brand.logo} />
            <div className="mb-3 px-5 pl-8 flex-grow flex items-end">
              <PropertiesNavigation active="user-management" />
            </div>
          </div>
          <div className="UserManagementBody content-minus-logo">
            <div style={{ maxWidth: '1750px' }} className="mx-auto">
              <BubbleOverlay hideBg loading={!dataBundle} loadingMinHeight="medium">
                <div
                  className="sticky w-full left-0 bg-blue-lightest"
                  style={{ top: '130px', height: '2rem', zIndex: 2 }}
                />
                {dataBundle && filteredUsers && (
                  <div className="px-10">
                    <div className="grid grid-cols-4 gap-4 items-start">
                      <div className="bg-white shadow-md rounded overflow-clip">
                        <div
                          className="sticky flex items-stretch bg-white border-b"
                          style={{ top: 'calc(130px + 2rem)', height: '50px', zIndex: 3 }}
                        >
                          <BubbleInput
                            type="text"
                            block
                            appearance="bare"
                            placeholder="Search..."
                            className="flex-grow"
                            inputStyle={{ height: '49px' }}
                            inputClassName="pl-5"
                            value={search}
                            onValue={setSearch}
                          />
                          <div className="dropdown-inline-fix">
                            <Dropdown
                              items={dropdownItems}
                              onSelect={onAction ? action => onAction(action) : undefined}
                            >
                              {({ toggleRef, ...rest }) => (
                                <BubbleButton
                                  buttonRef={toggleRef}
                                  {...rest}
                                  variant="navy"
                                  inverse
                                  icon="bars"
                                  className="h-full"
                                />
                              )}
                            </Dropdown>
                          </div>
                        </div>

                        {filteredUsers.map(u => (
                          <div
                            key={u._id}
                            className={`list-item px-5 py-3 cursor-pointer ${
                              activeUser && safeIdCompare(activeUser._id, u._id) ? 'active' : ''
                            }`}
                            onClick={() => setActiveUser(u)}
                          >
                            <div className="flex items-center">
                              <div className="flex-grow min-w-0">
                                <div className="list-item-name font-semibold overflow-ellipsis">
                                  {u.firstName} {u.lastName}
                                </div>
                                <div className="text-slate-dark overflow-ellipsis">
                                  {u.username}
                                </div>
                              </div>
                              <FontAwesomeIcon
                                icon="chevron-right"
                                className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                              />
                            </div>
                          </div>
                        ))}
                        {filteredUsers.length < 1 && (
                          <div
                            className="px-3 py-2 text-center font-italic text-slate
                              bg-eggshell"
                          >
                            No users found
                          </div>
                        )}
                      </div>
                      <div
                        className="sticky col-span-3"
                        style={{
                          top: 'calc(130px + 2rem)',
                          height: 'calc(100vh - 130px - 4rem)',
                        }}
                      >
                        {userForm && (
                          <BubbleValidated
                            form={userForm}
                            rules={userFormRules}
                            onValidSubmit={handleSubmitUserForm}
                          >
                            {({ trySubmit, validMap }) => (
                              <div className="bg-white border shadow-md max-h-full flex flex-col rounded overflow-clip">
                                <div className="flex-shrink-0 p-5 border-b bg-eggshell">
                                  {activeUser ? (
                                    <div className="flex">
                                      <div
                                        className="flex-shrink-0 mr-5"
                                        style={{
                                          width: '5rem',
                                          height: '5rem',
                                          borderRadius: '2.5rem',
                                          backgroundColor: hashStringToColor(activeUser.username),
                                        }}
                                      >
                                        <div
                                          className="text-2xl text-white w-full h-full
                                  text-center align-middle"
                                          style={{ lineHeight: '5rem' }}
                                        >
                                          {findInitials(
                                            [activeUser.firstName, activeUser.lastName],
                                            3
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex-grow">
                                        <div className="flex">
                                          <div
                                            className="flex-grow font-semibold
                                        text-navy-dark text-xl"
                                          >
                                            {activeUser.firstName} {activeUser.lastName}
                                          </div>
                                          <div className="flex-shrink-0">
                                            {recoveryToken ? (
                                              <BubbleButton
                                                variant="crimson"
                                                onClick={() => copyToClipboard(recoveryToken)}
                                                inverse
                                                className="p-0-i text-left"
                                                icon="copy"
                                              >
                                                {recoveryToken} (click to copy)
                                              </BubbleButton>
                                            ) : (
                                              <BubbleButton
                                                variant="crimson"
                                                onClick={() => handleGenerateRecoveryToken()}
                                                inverse
                                                className="p-0-i text-left"
                                                icon="key"
                                              >
                                                Generate Password Recovery Key
                                              </BubbleButton>
                                            )}
                                          </div>
                                        </div>
                                        <div className="text-slate-dark mb-1">
                                          {activeUser.username}
                                        </div>
                                        {activeUser.role.level === 'property' ? (
                                          <>
                                            <BubbleTypeahead
                                              block
                                              labelExtractor={el => el.name}
                                              placeholder="Add additional property..."
                                              menuClassName="max-h-80 overflow-y-auto"
                                              value={userForm.propertyAccessList}
                                              options={propertyOpts}
                                              onAdd={handleOnAddPropToAccess}
                                              onRemove={handleOnDeletePropFromAccess}
                                            />
                                            <div className="text-xs text-slate italic mt-1">
                                              NOTE:{' '}
                                              <span className="font-bold">
                                                {userForm.initialProperty.name}
                                              </span>{' '}
                                              cannot be deleted from this user's access
                                            </div>
                                          </>
                                        ) : (
                                          <div className="text-xs text-slate italic mt-1">
                                            <span className="font-bold">
                                              This user has access to all properties
                                            </span>{' '}
                                            and cannot be downgraded to property level.
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-2xl col-span-2 text-navy font-semibold">
                                      <h1>New User</h1>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-grow overflow-y-auto">
                                  <div className="p-5 grid grid-cols-2 gap-x-4 gap-y-6">
                                    {userForm.mode === 'create' && (
                                      <>
                                        <BubbleFormGroup label="Username" asterisk>
                                          <BubbleMask
                                            masks={['lowercased']}
                                            onMask={val => changeUserForm('username', val)}
                                          >
                                            {({ onValue }) => (
                                              <BubbleInput
                                                type="text"
                                                block
                                                value={userForm.username}
                                                onValue={onValue}
                                              />
                                            )}
                                          </BubbleMask>
                                          <BubbleValidatedStatus
                                            rules={userFormRules}
                                            validMap={validMap}
                                            keyToCheck="username"
                                          />
                                        </BubbleFormGroup>
                                        <BubbleFormGroup label="Initial Property">
                                          <BubbleSelect
                                            allowUndefined={false}
                                            options={properties}
                                            block
                                            value={userForm.initialProperty}
                                            labelExtractor={p => p.name}
                                            onValue={handleOnPropertySelect}
                                          />
                                        </BubbleFormGroup>
                                      </>
                                    )}
                                    <BubbleFormGroup label="First Name" asterisk>
                                      <BubbleInput
                                        type="text"
                                        block
                                        value={userForm.firstName}
                                        onValue={val => changeUserForm('firstName', val)}
                                      />
                                      <BubbleValidatedStatus
                                        rules={userFormRules}
                                        validMap={validMap}
                                        keyToCheck="firstName"
                                      />
                                    </BubbleFormGroup>
                                    <BubbleFormGroup label="Last Name" asterisk>
                                      <BubbleInput
                                        type="text"
                                        block
                                        value={userForm.lastName}
                                        onValue={val => changeUserForm('lastName', val)}
                                      />
                                      <BubbleValidatedStatus
                                        rules={userFormRules}
                                        validMap={validMap}
                                        keyToCheck="lastName"
                                      />
                                    </BubbleFormGroup>
                                    <div className="col-span-2">
                                      <BubbleFormGroup label="Email" asterisk>
                                        <BubbleMask
                                          masks={['lowercased']}
                                          onMask={val => changeUserForm('email', val)}
                                        >
                                          {({ onValue }) => (
                                            <BubbleInput
                                              type="text"
                                              block
                                              value={userForm.email}
                                              onValue={onValue}
                                            />
                                          )}
                                        </BubbleMask>
                                        <BubbleValidatedStatus
                                          rules={userFormRules}
                                          validMap={validMap}
                                          keyToCheck="email"
                                        />
                                      </BubbleFormGroup>
                                    </div>

                                    <div className="col-span-2">
                                      <BubbleFormGroup label="Basic Functions">
                                        <div className="grid gap-2">
                                          <BubbleCheckbox
                                            label="Canvas Editor"
                                            inputClassName="bg-white"
                                            checked={userForm.canvasEditor}
                                            onValue={val => changeUserForm('canvasEditor', val)}
                                            subtitle="Allows the user to edit the canvas (day calendar
                                              grid) on both the day and template level, which is
                                              required for adding events to a day/template or
                                              modifying options such as event merge variables.
                                              When combined with Library Editor permissions, this
                                              role also enables the creation of new events within
                                              the canvas."
                                          />
                                          <BubbleCheckbox
                                            label="Print Program Editor"
                                            inputClassName="bg-white"
                                            checked={userForm.feedEditor}
                                            onValue={val => changeUserForm('feedEditor', val)}
                                            subtitle="Enables the user to edit the print program layout
                                              (feed editor) on both the day and template level. It
                                              is necessary for modifying options such as content
                                              merge variables, line height, hours of operation,
                                              venue rundown, and more. When combined with Library
                                              Editor permissions, this role allows for the
                                              creation and editing of content within the print
                                              program."
                                          />
                                          <BubbleCheckbox
                                            label="Group User"
                                            inputClassName="bg-white"
                                            checked={userForm.groupUser}
                                            onValue={val => changeUserForm('groupUser', val)}
                                            subtitle="Enables the user to create new groups and group
                                              bookings. As a Group User, you can edit your own
                                              bookings and approve, reject, or change the status
                                              of any bookings. Your booking capabilities are
                                              limited to the specified time periods for venue
                                              space, as outlined in the Booking Hours section of
                                              the venue."
                                          />
                                          <BubbleCheckbox
                                            label="Reserve User"
                                            inputClassName="bg-white"
                                            checked={userForm.reserveUser}
                                            onValue={val => changeUserForm('reserveUser', val)}
                                            subtitle="Allows the user to view the Reserve tab and browse 
                                              available experiences. Does not grant the ability to manage 
                                              or create reservations and schedules. This permission works 
                                              as the “view-only” for reserve. Combine with other reserve 
                                              permissions to enhance privileges."
                                          />
                                          <BubbleCheckbox
                                            label="Experience Host (If Onboarding Complete)"
                                            inputClassName="bg-white"
                                            checked={userForm.host}
                                            onValue={val => changeUserForm('host', val)}
                                            subtitle="Allows the user to manage reservations for the
                                              experiences they host, including viewing guest bookings
                                              and managing their own schedule availability."
                                          />
                                        </div>
                                      </BubbleFormGroup>
                                    </div>
                                    <div className="col-span-2">
                                      <BubbleFormGroup label="Advanced Functions">
                                        <div className="grid gap-2">
                                          <BubbleCheckbox
                                            label="Library Editor"
                                            inputClassName="bg-white"
                                            checked={userForm.libraryAdmin}
                                            onValue={val => changeUserForm('libraryAdmin', val)}
                                            subtitle="Grants the user permission to create, modify, and
                                              archive property-specific library items — including
                                              events, content, venues, and staff — as well as
                                              access to the Translations tab. Modifications to
                                              brand-level items require Brand Admin permission and
                                              must be conducted on a brand instance (e.g. shore).
                                              When combined with Canvas & Print Program Editor it
                                              allows to create/edit library items on both a day
                                              and template level. This role does not provide
                                              permission to delete images from the library."
                                          />

                                          <BubbleCheckbox
                                            label="Group Requester"
                                            inputClassName="bg-white"
                                            checked={userForm.groupRequester}
                                            onValue={val => changeUserForm('groupRequester', val)}
                                            subtitle="Allows the user to create and edit group
                                              bookings in any venue space and date, without the
                                              Booking Hours restrictions, but does not allow them
                                              to approve, reject, or change the status of any
                                              booking. When combined with Group User, approvals
                                              remain limited to the specified time periods for
                                              venue space, as outlined in the Booking Hours
                                              section of the venue."
                                          />

                                          <BubbleCheckbox
                                            label="Group Admin"
                                            inputClassName="bg-white"
                                            checked={userForm.groupAdmin}
                                            onValue={val => changeUserForm('groupAdmin', val)}
                                            subtitle="Allows the user to approve, reject, or change the
                                              status of any group booking. Group Admin is an
                                              enhancement to Group User, removing limitations on
                                              venue booking times."
                                          />
                                          <BubbleCheckbox
                                            label="Reserve Admin"
                                            inputClassName="bg-white"
                                            checked={userForm.reserveAdmin}
                                            onValue={val => changeUserForm('reserveAdmin', val)}
                                            subtitle="Grants the user the ability to manage all schedule
                                              availability across all experiences, including adding
                                              or removing schedule rules and blocking exception
                                              dates. Also provides access to Reserve property
                                              settings."
                                          />
                                          <BubbleCheckbox
                                            label="Reservations Manager"
                                            inputClassName="bg-white"
                                            checked={userForm.reservationManager}
                                            onValue={val =>
                                              changeUserForm('reservationManager', val)
                                            }
                                            subtitle="Grants the user the ability to view and manage all
                                              reservations across all experiences and hosts."
                                          />
                                          <BubbleCheckbox
                                            label="Advanced Program Manager"
                                            inputClassName="bg-white"
                                            checked={userForm.shipAdmin}
                                            onValue={val => changeUserForm('shipAdmin', val)}
                                            subtitle="Grants the user the ability to lock and unlock days
                                              for both the Print Program and Canvas (Day Grid).
                                              This is the only permission level that allows
                                              editing locked days and creating or applying day
                                              templates. If itinerary management is configured,
                                              the user can also make changes such as adding or
                                              removing days, editing port details and times, and
                                              updating day themes."
                                          />
                                          <BubbleCheckbox
                                            label="User Manager"
                                            inputClassName="bg-white"
                                            checked={userForm.userAdmin}
                                            onValue={val => changeUserForm('userAdmin', val)}
                                            subtitle="Allows the management of GO users, including the
                                              creation and deletion of user accounts, as well as
                                              the ability to modify user access levels."
                                          />
                                          <BubbleCheckbox
                                            label="Property Settings Manager"
                                            inputClassName="bg-white"
                                            checked={userForm.propertyAdmin}
                                            onValue={val => changeUserForm('propertyAdmin', val)}
                                            subtitle="Allows the user to manage property settings,
                                              including name, description, hour format, and
                                              location. Depending on the client's enabled
                                              endpoints, this role may also have access to Digital
                                              settings, such as DDP and GO Mobile."
                                          />
                                          <BubbleCheckbox
                                            label="Brand Library & Settings Manager"
                                            inputClassName="bg-white"
                                            checked={userForm.brandAdmin}
                                            onValue={val => changeUserForm('brandAdmin', val)}
                                            subtitle="Grants the user the ability to edit Brand details,
                                              including attributes, levels, highlights, day
                                              themes, group types, meal periods, and more. This
                                              role also allows editing of Date & Time formats and
                                              the Common Texts section. When combined with Library
                                              Editor permission, the user has the ability to
                                              demote/promote Library Items to Brand/Multi as well
                                              as editing/archiving those. This role is also the
                                              only one that can translate the 'Brand Translations'
                                              tab and delete images from the Assets library. Note
                                              that all these changes can only be made within a
                                              Brand instance (shoreside environment)."
                                          />
                                        </div>
                                      </BubbleFormGroup>
                                    </div>
                                    <div className="col-span-2">
                                      <BubbleFormGroup label="Miscellaneous">
                                        <div className="grid gap-2">
                                          <BubbleCheckbox
                                            label="View Only"
                                            inputClassName="bg-white"
                                            checked={userForm.viewOnly}
                                            onValue={val => changeUserForm('viewOnly', val)}
                                            subtitle="Provides the user with viewing access only, removing
                                              all editing capabilities and overriding all other
                                              permissions."
                                          />
                                          <BubbleCheckbox
                                            label="Super Admin"
                                            inputClassName="bg-white"
                                            disabled
                                            checked={activeUser?.superAdmin ?? false}
                                            subtitle="Allows the user full access and the ability to
                                                override all other permissions. Important: This access 
                                                level can only be assigned and removed by the GO by Spark Team."
                                          />
                                        </div>
                                      </BubbleFormGroup>
                                    </div>
                                    {(!activeUser || activeUser.role.level === 'property') && (
                                      <>
                                        {dataBundle.venueTemplates.length > 0 && (
                                          <div className="col-span-2">
                                            <BubbleFormGroup label="Group Venue Approval List">
                                              <BubbleTypeahead
                                                block
                                                labelExtractor={el => el.name}
                                                placeholder="Search..."
                                                menuClassName="max-h-80 overflow-y-auto"
                                                value={userForm.groupApprovingVenues}
                                                options={dataBundle.venueTemplateOpts.filter(vopt =>
                                                  safeIdCompare(
                                                    vopt.item.property,
                                                    userForm.initialProperty._id
                                                  )
                                                )}
                                                onAdd={handleOnAddGroupApprovingVenues}
                                                onRemove={handleOnDeleteGroupApprovingVenues}
                                              />
                                            </BubbleFormGroup>
                                          </div>
                                        )}
                                        {dataBundle.staffTemplates.length > 0 && (
                                          <div className="col-span-2">
                                            <BubbleFormGroup label="Staff Role">
                                              <div className="flex -mx-2">
                                                <div className="flex-grow mx-2">
                                                  <BubbleSelect
                                                    allowUndefined={false}
                                                    disabled
                                                    value={userForm.initialProperty}
                                                    options={properties}
                                                    labelExtractor={p => p.name}
                                                    onValue={handleOnPropertySelect}
                                                    block
                                                  />
                                                </div>
                                                <div className="flex-grow mx-2">
                                                  <BubbleSelect
                                                    allowUndefined
                                                    value={userForm.staffRole}
                                                    options={dataBundle.staffTemplates.filter(st =>
                                                      safeIdCompare(
                                                        st.property,
                                                        userForm.initialProperty._id
                                                      )
                                                    )}
                                                    labelExtractor={st => st.title}
                                                    onValue={handleOnStaffRoleSelect}
                                                    block
                                                  />
                                                </div>
                                              </div>
                                            </BubbleFormGroup>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <div className="sticky bottom-0 p-3 z-2 border-t bg-eggshell flex items-center">
                                    {userForm.mode === 'edit' && activeUser && (
                                      <>
                                        {activeUser.role.level === 'property' && (
                                          <BubbleButton
                                            variant="velvet"
                                            onClick={() => promoteUserToBrand(activeUser)}
                                            icon="building"
                                          >
                                            Promote to Brand
                                          </BubbleButton>
                                        )}
                                        <BubbleButton
                                          variant="crimson"
                                          inverse
                                          icon="trash-alt"
                                          onClick={() => softDeleteUser(activeUser)}
                                        >
                                          Delete User
                                        </BubbleButton>
                                      </>
                                    )}
                                    <div className="mr-auto" />
                                    <BubbleButton
                                      variant="navy"
                                      inverse
                                      disabled={!userForm._dirty}
                                      onClick={() => resetUserForm(activeUser)}
                                      icon="rotate-left"
                                    >
                                      Reset
                                    </BubbleButton>
                                    <BubbleButton
                                      variant="navy"
                                      disabled={!userForm._dirty}
                                      onClick={trySubmit}
                                      icon={userForm.mode === 'create' ? 'sparkles' : 'floppy-disk'}
                                    >
                                      {userForm.mode === 'create' ? 'Create User' : 'Save Changes'}
                                    </BubbleButton>
                                  </div>
                                </div>
                              </div>
                            )}
                          </BubbleValidated>
                        )}
                      </div>
                    </div>
                    <div className="mt-3">&nbsp;</div>
                  </div>
                )}
              </BubbleOverlay>
            </div>
          </div>
        </div>
      </Page>
    </PageWrapper>
  );
}
